/* eslint-disable import/no-cycle */
/* eslint-disable react-hooks/rules-of-hooks */

import i18next from 'i18next';
import dayjs from 'dayjs';

import logoSeguritech from 'assets/SeguriTech';
import { Client, ClientLocationDetail, ClientService } from 'api/client';
import {
  GetClosedCommentsToTicket,
  GetCommentListByTicketResponse,
  IReportConfig,
  TicketDetail
} from 'api/ticket';
import { ProductForTicket } from 'api/product';
import { AccesoryResponse, SupplyResponse } from 'api/inventory';
import { removeEmojis } from 'helpers/utils';
import {
  stripItilTranslationFromComment,
  stripLegacyCloseItilFromCommentDetails
} from 'features/itilGenius';

import * as pdfMakeUtils from '../pdfMakeUtils';
import getIncidentOrderInfoTable from './getIncidentOrderInfoTable';
import getIncidentDescriptionTable from './getIncidentDescriptionTable';
import {
  getPdfClosedDateTime,
  getPdfPendingValidationDateTime
} from './getPdfCloseDateTime';
// import getSiteSupportTable from './getSiteSupportTable';

/** Tipo de comentario "Cierre" en bitácora; puede repetirse al guardar, en PDF va una sola vez. */
const CLOSURE_COMMENT_TYPE_ID = 125;

export interface ITicketDetail extends TicketDetail {
  ticketProduct: ProductForTicket[];
}

export type TicketData = {
  clientActive: Client;
  projectAndService: ClientService;
  dataIncident: ITicketDetail;
  location: ClientLocationDetail;
  supplies: SupplyResponse[];
  installedAccesories: AccesoryResponse[];
  comments: GetCommentListByTicketResponse[];
  closedTicketComments?: GetClosedCommentsToTicket;
  reportConfig?: IReportConfig;
  pdfOverrides?: import('features/itilGenius').PdfItilOverrides;
};

/** Claves i18n de los campos de folio que no deben mostrarse en el PDF de cierre. */
const INVOICE_FOLIO_I18N_KEYS = [
  'admin.catalogs.invoice.closingFields.expenseCode',
  'admin.catalogs.invoice.closingFields.fuelCode',
  'admin.catalogs.invoice.closingFields.tagCode',
  'drawer.admin.catalogues.invoiceTAG'
] as const;

const getInvoiceFolioFieldNames = (): string[] => [
  ...new Set(
    INVOICE_FOLIO_I18N_KEYS.map((key) => i18next.t(key, { ns: 'global' }))
  )
];

/**
 * Filtra las líneas del comentario de cierre eliminando las que corresponden
 * a campos marcados con isVisibleInPdf = false en el catálogo del servicio
 * y los folios de viáticos / combustible / TAG (siempre ocultos en PDF).
 */
const filterClosingComment = (
  comment: string | undefined,
  closingFields: ITicketDetail['ticketServiceTypeClosingFields']
): string => {
  if (!comment) return '';

  const excludedPrefixes = [
    ...(closingFields ?? [])
      .filter((f) => !f.isVisibleInPdf)
      .map((f) => `${f.name}:`),
    ...getInvoiceFolioFieldNames().map((name) => `${name}:`)
  ];

  if (excludedPrefixes.length === 0) return comment;

  return comment
    .split('\n')
    .filter(
      (line) =>
        !excludedPrefixes.some((prefix) =>
          line.trimStart().toLowerCase().startsWith(prefix.toLowerCase())
        )
    )
    .join('\n');
};

/** Quita secciones `## Folio…` del texto consolidado ITIL de comentarios de cierre. */
const filterConsolidatedCloseComments = (
  comment: string | undefined
): string => {
  if (!comment?.trim()) return '';
  if (!comment.includes('##')) return comment;

  const folioNames = getInvoiceFolioFieldNames().map((name) =>
    name.toLowerCase()
  );

  return comment
    .split(/\n(?=## )/)
    .filter((section) => {
      const label = section
        .replace(/^##\s*/, '')
        .split('\n')[0]
        ?.trim();
      return !folioNames.includes(label?.toLowerCase() ?? '');
    })
    .join('\n')
    .trim();
};

const resolveCloseCommentsForPdf = (ticketData: TicketData): string => {
  const closingFields = ticketData.dataIncident?.ticketServiceTypeClosingFields;

  if (ticketData.pdfOverrides?.closeComments) {
    return filterClosingComment(
      filterConsolidatedCloseComments(ticketData.pdfOverrides.closeComments),
      closingFields
    );
  }

  return filterClosingComment(
    stripLegacyCloseItilFromCommentDetails(
      ticketData.closedTicketComments?.commentDetails
    ),
    closingFields
  );
};

const getPdfTemplate = (
  ticketData: TicketData,
  pdfImages: string[],
  imageDescription: string[],
  logo?: string
) => {
  const pageWidth = 595;

  let closureCommentIncluded = false;
  const pdfComments =
    ticketData.comments
      ?.filter((comment) => {
        if (comment.commentTypeId !== CLOSURE_COMMENT_TYPE_ID) {
          return true;
        }
        if (closureCommentIncluded) {
          return false;
        }
        closureCommentIncluded = true;
        return true;
      })
      ?.map((comment) => ({
        ...comment,
        comment: stripItilTranslationFromComment(comment?.comment ?? '')
      }))
      .filter((comment) => comment.comment?.trim()) ?? [];

  const images = pdfImages?.reduce(
    (acc, item) => ({
      ...acc,
      [item]: item
    }),
    {}
  );

  const serviceTitle = () => {
    if (!ticketData?.reportConfig?.serviceTitle) return '';
    if (typeof ticketData?.reportConfig?.serviceTitle === 'string') {
      return ticketData?.reportConfig?.serviceTitle.toUpperCase();
    }
    return '';
  };

  const clientName = () => {
    if (!ticketData?.reportConfig?.clientName) return '';
    if (typeof ticketData?.reportConfig?.clientName === 'string') {
      return ticketData?.reportConfig?.clientName;
    }
    return '';
  };

  const requestedByUser = () => {
    if (!ticketData?.reportConfig?.requestedBy) return '';
    if (typeof ticketData?.reportConfig?.requestedBy === 'string') {
      return ticketData?.reportConfig?.requestedBy;
    }
    return '';
  };

  const requestedByPosition = () => {
    if (!ticketData?.reportConfig?.requestedByPosition) return '';
    if (typeof ticketData?.reportConfig?.requestedByPosition === 'string') {
      return ticketData?.reportConfig?.requestedByPosition;
    }
    return '';
  };

  const clientPosition = () => {
    if (!ticketData?.reportConfig?.clientPosition) return '';
    if (typeof ticketData?.reportConfig?.clientPosition === 'string') {
      return ticketData?.reportConfig?.clientPosition;
    }
    return '';
  };

  const requestedBySignatureTitle = () => {
    if (!ticketData?.reportConfig?.requestedBySignatureTitle) return '';
    if (
      typeof ticketData?.reportConfig?.requestedBySignatureTitle === 'string'
    ) {
      return ticketData?.reportConfig?.requestedBySignatureTitle;
    }
    return '';
  };

  const clientSignatureTitle = () => {
    if (!ticketData?.reportConfig?.clientSignatureTitle) return '';
    if (typeof ticketData?.reportConfig?.clientSignatureTitle === 'string') {
      return ticketData?.reportConfig?.clientSignatureTitle;
    }
    return '';
  };

  const imagesRows = pdfImages?.map((image, index) => [
    {
      image,
      width: pageWidth * 0.4,
      alignment: 'center'
    },
    {
      text: imageDescription[index] || '',
      alignment: 'center',
      style: 'titleBold',
      margin: [0, 10, 0, 0]
    }
  ]);

  const chunk = (arr: [], len: number) => {
    const chunks = [];
    let i = 0;
    const n = arr?.length;

    while (i < n) {
      chunks?.push(arr?.slice(i, (i += len)));
    }

    return chunks;
  };

  const { pdfMake } = window;

  pdfMake.tableLayouts = {
    ...pdfMake.tableLayouts,
    customTheme: pdfMakeUtils.customTheme.layout,
    borderedContainer: pdfMakeUtils.borderedContainer,
    tableBorderedContainer: pdfMakeUtils.tableBorderedContainer
  };

  const installedProducts =
    ticketData.dataIncident?.ticketProduct?.filter(
      (p: ProductForTicket) => p?.productConditionId === 1
    ) || [];
  const retiredProducts =
    ticketData.dataIncident?.ticketProduct?.filter(
      (p: ProductForTicket) => p?.productConditionId === 4
    ) || [];
  const totalProducts = installedProducts.length + retiredProducts.length;

  // Cuenta las líneas de los comentarios
  const commentsLines =
    pdfComments.reduce(
      (acc, comment) => acc + (comment?.comment?.split('\n').length || 0),
      0
    ) || 0;

  // Comentario de cierre filtrado (folios + campos con isVisibleInPdf = false)
  const filteredClosingComment = resolveCloseCommentsForPdf(ticketData);

  const closedCommentsLines = filteredClosingComment
    .split('\n')
    .filter(Boolean).length;

  // Total de líneas de comentarios
  const totalCommentsLines = commentsLines + closedCommentsLines;

  const content = {
    pageSize: 'A4',
    pageMargins: [20, 40, 20, 40],
    defaultStyle: pdfMakeUtils.customTheme.defaultStyle,
    styles: pdfMakeUtils.customTheme.styles,
    footer: () => ({
      text: `${i18next.t('ticket.properties.footerPDFLabel', {
        ns: 'global'
      })}`,
      alignment: 'right',
      fontSize: 7, // Tamaño de fuente directamente aquí
      margin: [0, 0, 20, 0]
    }),
    header: (currentPage: number) => {
      if (currentPage > 1) {
        return {
          columns: [
            {
              text: '',
              width: pageWidth * 0.2
            },
            {
              text: '',
              width: '45%'
            },
            {
              text: `${i18next.t('costReport.columns.ticketID', {
                ns: 'global'
              })}: ${ticketData?.dataIncident?.ticketId || ''}`,
              style: 'titleRight',
              width: 'auto'
            }
          ],
          margin: [0, 20]
        };
      }
      return null;
    },
    content: [
      ...(serviceTitle()
        ? [
            {
              text: serviceTitle(),
              style: 'titleBold',
              alignment: 'left',
              margin: [0, 0, 0, 10]
            }
          ]
        : []),
      {
        columns: [
          {
            image:
              ticketData?.clientActive?.blobStorageUrlPath && logo
                ? `${logo}`
                : `data:image/png;base64,${logoSeguritech}`,
            width: pageWidth * 0.2
          },
          {
            text: `${i18next.t('ticket.pdf.order', {
              ns: 'global'
            })}\n${ticketData?.dataIncident?.ticketServiceName || ''}`,
            style: 'titleBold',
            width: '55%'
          },

          {
            text: `${i18next.t('costReport.columns.ticketID', {
              ns: 'global'
            })}: ${ticketData?.dataIncident?.ticketId || ''}`,
            style: 'titleRight',
            width: '25%',
            margin: [0, 0, 0, 50]
          }
        ]
      },
      ' ',
      getIncidentOrderInfoTable(ticketData),
      ' ',
      getIncidentDescriptionTable(ticketData),
      ' ',
      {
        table: {
          widths: ['*', '*'],
          body: [
            [
              {
                stack: [
                  pdfMakeUtils.BorderedCell(
                    `${i18next.t('common.commonProperties.invoice', {
                      ns: 'global'
                    })}:\n${ticketData?.dataIncident?.ticketId || ''}`
                  ),
                  pdfMakeUtils.CellSeparator(),
                  pdfMakeUtils.BorderedCell(
                    `${i18next.t('ticket.pdf.siteDateTime', {
                      ns: 'global'
                    })}:\n${
                      ticketData?.dataIncident?.ticketWorkingDateTime
                        ? dayjs
                            .utc(
                              ticketData?.dataIncident?.ticketWorkingDateTime
                            )
                            .local()
                            .format('DD/MM/YYYY HH:mm:ss')
                        : ''
                    }`
                  ),
                  pdfMakeUtils.CellSeparator(),
                  pdfMakeUtils.BorderedCell(
                    `${i18next.t('ticket.pdf.closeDateTime', {
                      ns: 'global'
                    })}:\n${getPdfClosedDateTime(ticketData?.dataIncident)}`
                  )
                ]
              },
              {
                stack: [
                  pdfMakeUtils.BorderedCell(
                    `${i18next.t('common.commonProperties.taskForce', {
                      ns: 'global'
                    })}:\n${
                      ticketData?.dataIncident
                        ?.ticketAssignedEngineerFullName || ''
                    }`
                  ),
                  pdfMakeUtils.CellSeparator(),
                  pdfMakeUtils.BorderedCell(
                    `${i18next.t('ticket.pdf.pendingValidationDateTime', {
                      ns: 'global'
                    })}:\n${getPdfPendingValidationDateTime(
                      ticketData?.dataIncident
                    )}`
                  )
                ]
              }
            ]
          ]
        },
        layout: 'tableBorderedContainer',
        margin: [0, 0, 0, 0],
        pageBreak: 'after'
      },
      ' ',
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                text: `${i18next.t('ticket.pdf.closeComments', {
                  ns: 'global'
                })}`,
                style: 'titleBold'
              }
            ],
            [
              (() => {
                const closeText = filteredClosingComment;
                return closeText?.split('\n')?.filter(Boolean).length > 0
                  ? [
                      {
                        ul: closeText
                          .split('\n')
                          .filter(Boolean)
                          .map((comment) => removeEmojis(comment))
                      }
                    ]
                  : [
                      `${i18next.t('ticket.pdf.noComment', {
                        ns: 'global'
                      })}`
                    ];
              })()
            ]
          ]
        },
        layout: 'borderedContainer'
      },
      ' ',
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                text: `${i18next.t('ticket.pdf.comment', {
                  ns: 'global'
                })}`,
                style: 'titleBold'
              }
            ],
            pdfComments.length > 0
              ? [
                  {
                    ul: pdfComments.map((comment) =>
                      removeEmojis(
                        filterClosingComment(
                          comment.comment,
                          ticketData.dataIncident
                            ?.ticketServiceTypeClosingFields
                        )
                      )
                    )
                  }
                ]
              : [
                  {
                    ul: [
                      `${i18next.t('ticket.pdf.noComment', {
                        ns: 'global'
                      })}`
                    ]
                  }
                ]
          ]
        },
        layout: 'borderedContainer'
      },
      ' ',
      (() => ({
        table: {
          widths: ['*'],
          body: [
            [
              {
                text: `${i18next.t('ticket.pdf.totalComponents', {
                  ns: 'global'
                })}`,
                style: 'titleBold'
              }
            ],
            [
              `${i18next.t('ticket.pdf.installed', {
                ns: 'global'
              })}`
            ],
            installedProducts.length > 0
              ? [
                  {
                    ul: installedProducts.map(
                      (product: ProductForTicket) =>
                        `${i18next.t('common.commonProperties.serialn', {
                          ns: 'global'
                        })}: ${product?.serialNumber || ''} - ${i18next.t(
                          'common.commonProperties.description',
                          {
                            ns: 'global'
                          }
                        )}: ${product?.modelDescription || ''} - ${i18next.t(
                          'common.commonProperties.model',
                          {
                            ns: 'global'
                          }
                        )}: ${product?.modelName || ''} - ${i18next.t(
                          'common.commonProperties.brand',
                          {
                            ns: 'global'
                          }
                        )}: ${product?.brandName || ''}`
                    )
                  }
                ]
              : [
                  {
                    ul: [
                      `${i18next.t('ticket.pdf.noComment', {
                        ns: 'global'
                      })}`
                    ]
                  }
                ],
            [
              `${i18next.t('ticket.pdf.retired', {
                ns: 'global'
              })}`
            ],
            retiredProducts.length > 0
              ? [
                  {
                    ul: retiredProducts.map(
                      (product: ProductForTicket) =>
                        `${i18next.t('common.commonProperties.serialn', {
                          ns: 'global'
                        })}: ${product?.serialNumber || ''} - ${i18next.t(
                          'common.commonProperties.description',
                          {
                            ns: 'global'
                          }
                        )}: ${product?.modelDescription || ''} - ${i18next.t(
                          'common.commonProperties.model',
                          {
                            ns: 'global'
                          }
                        )}: ${product?.modelName || ''} - ${i18next.t(
                          'common.commonProperties.brand',
                          {
                            ns: 'global'
                          }
                        )}: ${product?.brandName || ''}`
                    )
                  }
                ]
              : [
                  {
                    ul: [
                      `${i18next.t('ticket.pdf.noComment', {
                        ns: 'global'
                      })}`
                    ]
                  }
                ]
          ]
        },
        layout: 'borderedContainer',
        keepTogether: totalProducts <= 10,
        pageBreak:
          totalProducts > 5 && totalCommentsLines > 10 ? 'before' : undefined
      }))(),
      ' ',
      (() => {
        // Calcular el ancho de cada columna de firma (considerando márgenes de página)
        const signatureColumnWidth = (pageWidth - 40) / 2 - 10; // 40 = márgenes, 10 = padding
        return {
          table: {
            widths: [signatureColumnWidth, signatureColumnWidth],
            heights: 100,
            body: [
              [
                {
                  stack: [
                    {
                      text: requestedBySignatureTitle()
                        ? requestedBySignatureTitle()
                        : `${i18next.t('ticket.pdf.nameAndSignature', {
                            ns: 'global'
                          })}`,
                      width: signatureColumnWidth
                    }
                  ],
                  style: 'signCell'
                },
                {
                  stack: [
                    {
                      text: clientSignatureTitle()
                        ? clientSignatureTitle()
                        : `${i18next.t('ticket.pdf.nameAndSignature', {
                            ns: 'global'
                          })}`,
                      width: signatureColumnWidth
                    }
                  ],
                  style: 'signCell'
                }
              ],
              [
                {
                  stack: [
                    {
                      text: `${requestedByUser()}${
                        requestedByPosition()
                          ? `\n\n${requestedByPosition()}`
                          : ''
                      }`,
                      width: signatureColumnWidth
                    }
                  ],
                  style: 'signCell'
                },
                {
                  stack: [
                    {
                      text: `${clientName()}${
                        clientPosition() ? `\n\n${clientPosition()}` : ''
                      }`,
                      width: signatureColumnWidth
                    }
                  ],
                  style: 'signCell'
                }
              ]
            ]
          },
          layout: 'borderedContainer',
          pageBreak:
            totalProducts > 15 || totalCommentsLines > 20
              ? 'before'
              : undefined,
          keepTogether: true
        };
      })(),
      ' ',
      imagesRows?.length > 0
        ? {
            table: {
              widths: ['*'],
              body: [
                [
                  {
                    text: `${i18next.t('ticket.pdf.evidence', {
                      ns: 'global'
                    })}`,
                    style: 'titleBold'
                  }
                ],
                ...chunk(
                  imagesRows.length % 2 === 1
                    ? [...imagesRows, [{}]]
                    : imagesRows,
                  2
                ).map((row) => [
                  {
                    columns: row,
                    columnGap: 20,
                    noWrap: false
                  }
                ])
              ]
            },
            layout: 'borderedContainer'
          }
        : ''
    ],
    images
  };

  return content;
};

export default getPdfTemplate;
